'use client';

import dayjs from 'dayjs';
import clonedeep from 'lodash/cloneDeep';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { type Item as UsptoItem } from '~/server/utils/processors/policyEngine/policies/searchUspto';
import { api, type RouterOutputs } from '~/utils/api';
import { cn } from '~/lib/utils';

import { CheckCircleIcon } from '@heroicons/react/24/outline';
import {
  ClaimAndTrademarkApproval,
  ContentMedium,
  PolicyReviewStatus,
  RiskLevel,
  type DocumentSource,
  type Trademark,
} from '@prisma/client';

import { confidenceThreshold, SpecialPolicyFeedbackLabel, StatusOptions } from '~/config';
import { type FeedbackLocation } from '~/server/utils/processors/document/processPages';
import { getIsApproved, getTmTexts, USTPO_TEXT } from '~/utils/trademark';
import HtmlContent from '../HtmlContent';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../ui/hover-card';
import ConfirmClaimModal from './ConfirmClaimModal';
import ConfirmTrademarkModal from './ConfirmTrademarkModal';
import FeedbackCard, { type ApprovalStatus } from './FeedbackCard';
import FeedbackListExitRow from './FeedbackListExitRow';
import {
  FeedbackListExitLifecycle,
  type FeedbackExitCommit,
  type FeedbackExitReason,
} from './feedbackListExitLifecycle';
import { getIsLastOpenIssue } from './getIsLastOpenIssue';
import { getIsRowVisible } from './getIsRowVisible';
import PolicyCard from './PolicyCard';
import { hasTruePositiveMark } from './ThumbsUp';
import SimilarUsages from './SimilarUsages';
import { type TranscriptionVerbose } from 'openai/resources/audio/transcriptions';
import Link from 'next/link';
import { useWorkspace } from '~/contexts/WorkspaceContext';
import { policyEnginePath } from '~/utils/policyEnginePath';

type Document = NonNullable<RouterOutputs['scanner']['getCrawledPage']>;
type Claim = Document['claim'][0];
type TrademarkInstance = Document['trademarkInstance'][0];
type PolicyFeedback = Document['policyFeedback'][0] & { displayText?: string };
export type Feedback = Claim | TrademarkInstance | PolicyFeedback;
export type SortedClaim = { feedbackType: 'claim' } & Claim;
export type SortedTmInstance = { feedbackType: 'trademark' } & TrademarkInstance;

export type SortedPolicyFeedback = {
  feedbackType: 'policyFeedback';
  /** concatenate feedback ids */
  feedbackIds?: string[];
} & PolicyFeedback;
export type SortedFeedback = SortedClaim | SortedTmInstance | SortedPolicyFeedback;

type SuggestionsAndSimilarUsages =
  RouterOutputs['feedback']['getFeedbackSuggestionsAndSimilarUsages'];

export const getIsClaim = (feedback: Feedback): feedback is Claim =>
  typeof (feedback as Claim).claim === 'string';

export const getIsTrademarkInstance = (feedback: Feedback): feedback is TrademarkInstance =>
  !!(feedback as TrademarkInstance).trademarkId;

export const getIsPolicyFeedback = (feedback: Feedback): feedback is PolicyFeedback =>
  !getIsClaim(feedback) && !!(feedback as PolicyFeedback).policy;

type ScannedPage = RouterOutputs['scanner']['getCrawledPage'];

export type FeedbackListProps = {
  docType?: DocumentSource;
  feedbacks: Feedback[];
  suggestionsAndSimilarUsages?: SuggestionsAndSimilarUsages | undefined;
  evaluationType: ScannedPage['evaluationType'] | undefined | null;
  onComment?: (feedback: Feedback) => void;
  onRemove?: (feedbackId: string) => void;
  reloadDoc: () => Promise<void>;
  onItemClick?: (
    e: MouseEvent<HTMLDivElement, globalThis.MouseEvent>,
    text: string,
    location: FeedbackLocation,
  ) => void;
  isVideo: boolean;
  brand: { id: string; name: string } | null;
  documentId: string;
  isMultiPages: boolean;
  pages?: ScannedPage['pages'];
  isTest?: boolean;
  isContentScanner?: boolean;
  isResolvedFeedback: boolean;
  totalFeedbacks: number;
  hiddenHtmlText?: string;
  isPublicView?: boolean; // New prop for public shareable links
  isCompactView?: boolean;
  isInShadowMode?: boolean;
  onLastIssueExitStart?: () => void;
  onLastIssueExitRestore?: () => void;
  /** Called when a resolve action finishes its exit animation (open tray only). */
  onResolvedExit?: (feedbackIds: string[]) => void;
};

export type FeedbackListExitHandlers = {
  beginExit: (
    feedbackId: string,
    reason: FeedbackExitReason,
    mutation: Promise<unknown>,
    relatedIds?: string[],
  ) => void;
  restoreExit: (feedbackId: string) => void;
  isRowExiting?: (feedbackId: string) => boolean;
};

type FeedbackListItemProps = FeedbackListProps & FeedbackListExitHandlers;

export const TrademarkFeedback = ({
  feedback,
  proprietary,
  approval,
  isCompactView,
}: {
  feedback: Pick<
    TrademarkInstance,
    'suggestedMarkRecord' | 'suggestedMarkExplanation' | 'suggestedMark'
  >;
  proprietary: boolean;
  approval: Trademark['approval'];
  isCompactView?: boolean;
}) => {
  const texts = getTmTexts(feedback, proprietary, approval);

  const suggestedMarkRecord = feedback.suggestedMarkRecord as unknown as UsptoItem | null;

  return (
    <>
      {texts.map((t, i) => (
        <div key={i} className={cn('text-muted-foreground', isCompactView ? 'text-xs' : 'text-sm')}>
          {!t.startsWith(USTPO_TEXT) ? (
            t
          ) : (
            <HoverCard openDelay={200} closeDelay={100}>
              <HoverCardTrigger asChild>
                <button
                  type="button"
                  className="hover:text-foreground text-left underline decoration-dotted underline-offset-2"
                >
                  {t}
                </button>
              </HoverCardTrigger>
              <HoverCardContent align="start" className="w-72 space-y-2 p-3 text-xs">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-muted-foreground font-semibold">Record #</div>
                  <div>{suggestedMarkRecord?.registration_number}</div>
                </div>
                <div className="border-border border-t" />
                <div className="flex items-center justify-between gap-3">
                  <div className="text-muted-foreground font-semibold">Registration Date</div>
                  <div>
                    {suggestedMarkRecord?.registration_date
                      ? dayjs(suggestedMarkRecord?.registration_date).format('MMM D, YYYY')
                      : 'N/A'}
                  </div>
                </div>
                <div className="border-border border-t" />
                <div className="flex items-center justify-between gap-3">
                  <div className="text-muted-foreground font-semibold">Owner</div>
                  <div className="max-w-[150px] truncate">
                    {suggestedMarkRecord?.owners?.[0]?.name}
                  </div>
                </div>
              </HoverCardContent>
            </HoverCard>
          )}
        </div>
      ))}
    </>
  );
};

const getSortedFeedbacks = (feedbacks: Feedback[], isVideo: boolean, isInShadowMode?: boolean) => {
  // clone the feedback and sort by text length desc so we can concat the feedback
  const sortedAllFeedback: SortedPolicyFeedback[] = clonedeep(feedbacks).sort((a, b) =>
    (a.text?.length || 0) > (b.text?.length || 0) ? -1 : 1,
  ) as SortedPolicyFeedback[];
  const riskLevelRanks: Record<RiskLevel, 0 | 1 | 2 | 3> = {
    [RiskLevel.AUTO]: 0,
    [RiskLevel.LOW]: 1,
    [RiskLevel.MEDIUM]: 2,
    [RiskLevel.HIGH]: 3,
  };

  const RankToRisk: Record<0 | 1 | 2 | 3, RiskLevel> = {
    0: RiskLevel.AUTO,
    1: RiskLevel.LOW,
    2: RiskLevel.MEDIUM,
    3: RiskLevel.HIGH,
  };

  // Skip merging in shadow mode - just sort and return
  if (!isInShadowMode) {
    let i = 0;
    while (i < sortedAllFeedback.length - 1) {
      const current = sortedAllFeedback[i] as SortedPolicyFeedback;
      const next = sortedAllFeedback[i + 1] as SortedPolicyFeedback;
      if (
        !getIsClaim(current) &&
        !getIsTrademarkInstance(current) &&
        !getIsClaim(next) &&
        !getIsTrademarkInstance(next) &&
        next.text &&
        current.text?.trim().toLowerCase().includes(next.text.trim().toLowerCase())
      ) {
        // concat the policy name and explanation
        if (
          current.policyId !== next.policyId &&
          !current.feedbackIds?.some((id) => {
            const feedback = feedbacks.find((f) => f.id === id);
            return (feedback as PolicyFeedback)?.policyId === next.policyId;
          })
        ) {
          current.policy.name = current.policy.name.concat(`, ${next.policy.name}`);
        }
        current.feedbackIds = [...(current.feedbackIds || [current.id]), next.id];

        current.riskLevel =
          RankToRisk[
            Math.max(
              riskLevelRanks[current.riskLevel ?? RiskLevel.LOW],
              riskLevelRanks[next.riskLevel ?? RiskLevel.LOW],
            ) as 0 | 1 | 2
          ];
        // replace with the updated current
        sortedAllFeedback.splice(i, 1, current);

        // remove the next because it's been merged to current
        sortedAllFeedback.splice(i + 1, 1);
      } else {
        i++;
      }
    }
  }

  // sort by location
  sortedAllFeedback.sort((a, b) => {
    const locationA = a.location as FeedbackLocation | undefined;
    const locationB = b.location as FeedbackLocation | undefined;
    if (isVideo) {
      return (locationA?.videoStartTime || Infinity) - (locationB?.videoStartTime || Infinity);
    } else {
      return (locationA?.pageNumber || Infinity) - (locationB?.pageNumber || Infinity) !== 0
        ? (locationA?.pageNumber || Infinity) - (locationB?.pageNumber || Infinity)
        : (locationA?.position || Infinity) - (locationB?.position || Infinity);
    }
  });

  return sortedAllFeedback;
};

const getConcatenateFeedbacks = (sortedFeedbacks: Feedback[], feedback: SortedPolicyFeedback) => {
  return (
    feedback.feedbackIds?.length
      ? sortedFeedbacks.filter((i) => feedback.feedbackIds?.includes(i.id))
      : [feedback]
  ) as SortedPolicyFeedback[];
};

const getConcatenatedSuggestions = (
  sortedFeedbacks: SortedFeedback[],
  feedback: SortedPolicyFeedback,
) => {
  const concatenateFeedbacks = getConcatenateFeedbacks(sortedFeedbacks, feedback);

  return Array.from(
    new Set(
      concatenateFeedbacks
        .map((i) => i.PolicyFeedbackSuggestion?.map((s) => s.suggestion) || [])
        .flat(),
    ),
  );
};

const getDocumentText = (
  text: string,
  feedbackLocation: ContentMedium,
  page: ScannedPage['pages'][0],
  hiddenHtmlText?: string,
) => {
  let sourceText = '';

  if (
    feedbackLocation === ContentMedium.IMAGE_OCR_TEXT ||
    feedbackLocation === ContentMedium.WEB_HIDDEN_TEXT
  ) {
    sourceText = (page.images[0]?.ocrResults[0]?.text || '') + ' ' + (hiddenHtmlText || '');
  }

  if (feedbackLocation === ContentMedium.VIDEO_UNIQUE_OCR_TEXT) {
    sourceText = (page.videos[0]?.uniqueOcrText as Array<string>)?.join(' ');
  }

  if (feedbackLocation === ContentMedium.VIDEO_TRANSCRIPT) {
    sourceText = (page.videos[0]?.transcription as unknown as TranscriptionVerbose)?.text;
  }

  return sourceText ?? text ?? '';
};

type ClaimFeedbackProps = {
  feedback: Claim;
} & FeedbackListItemProps;

const ClaimFeedback = (props: ClaimFeedbackProps) => {
  const { feedback, suggestionsAndSimilarUsages } = props;
  const { mutateAsync: actionPolicyFeedback } = api.feedback.actionPolicyFeedback.useMutation();

  let approvalStatus: ApprovalStatus | undefined = undefined;
  const claim = suggestionsAndSimilarUsages?.results.claims.find((i) => i.id === feedback.id);
  const isApproved =
    getIsApproved(feedback.approval) ||
    !!claim?.similarUsages?.find(
      (c) => getIsApproved(c.approval) && c.confidence >= confidenceThreshold.high,
    );

  if (isApproved) {
    approvalStatus = 'Approved';
  }

  if (!approvalStatus) {
    const isLikelyApproved = !!claim?.similarUsages?.find(
      (c) =>
        c.approval === ClaimAndTrademarkApproval.IN_USE_CONSIDER_REVIEW &&
        c.confidence >= confidenceThreshold.high,
    );
    if (isLikelyApproved) {
      approvalStatus = 'Likely Approved';
    }
  }

  return (
    <FeedbackCard
      key={feedback.id}
      isClaim
      feedbackId={feedback.id}
      isShadowFeedback={(feedback as any).isShadowFeedback}
      shadowModelName={(feedback as any).shadowModelName}
      isPrimaryFeedback={(feedback as any).isPrimaryFeedback}
      primaryModelName={(feedback as any).primaryModelName}
      policy={
        feedback.Policy
          ? {
              ...feedback.Policy,
              hasVisionPrompt: feedback.Policy.steps?.some((s) => !!s.visionPrompt) || false,
            }
          : null
      }
      brand={props.brand}
      documentId={props.documentId}
      evaluationType={props.evaluationType}
      reloadDoc={props.reloadDoc}
      isTest={props.isTest}
      isResolvedFeedback={props.isResolvedFeedback}
      riskLevel={feedback.riskLevel}
      contentMedium={feedback.feedbackLocation}
      videoStartTime={(feedback.location as FeedbackLocation)?.videoStartTime}
      color={
        feedback.Policy?.feedbackLabel?.color || SpecialPolicyFeedbackLabel.STANDARD_CLAIM.color
      }
      isNew={feedback.isNew}
      approvalStatus={approvalStatus}
      description={feedback.text}
      alreadyMarked={hasTruePositiveMark([feedback])}
      suggestions={
        isApproved || props.isPublicView || (feedback as any).isShadowFeedback
          ? []
          : claim?.suggestions.map((s) => s.suggestion)
      }
      title={
        feedback.Policy
          ? feedback.Policy?.feedbackLabel?.name || feedback.Policy.name
          : SpecialPolicyFeedbackLabel.STANDARD_CLAIM.name
      }
      confirmButton={
        isApproved || props.isPublicView || (feedback as any).isShadowFeedback ? undefined : (
          <ConfirmClaimModal claimId={feedback.id} reloadDoc={props.reloadDoc} />
        )
      }
      onClick={(e) => props.onItemClick?.(e, feedback.text, feedback.location as FeedbackLocation)}
      onComment={
        !isApproved && props.onComment && !props.isPublicView && !(feedback as any).isShadowFeedback
          ? () => props.onComment?.(feedback)
          : undefined
      }
      beginExit={props.beginExit}
      restoreExit={props.restoreExit}
      isRowExiting={props.isRowExiting}
      onRemove={props.onRemove}
      onResolve={async () => {
        await actionPolicyFeedback({
          claim: {
            id: feedback.id,
            status: props.isResolvedFeedback ? StatusOptions.UNVERIFIED : StatusOptions.SKIPPED,
          },
        });
      }}
      isMultiPages={props.isMultiPages}
      feedbackLocation={feedback.location as FeedbackLocation}
      isPublicView={props.isPublicView}
      isInShadowMode={props.isInShadowMode}
    >
      {!isApproved && feedback.riskExplanation && (
        <HtmlContent
          className={cn(
            'text-foreground leading-relaxed',
            props.isCompactView ? 'text-xs' : 'text-sm',
          )}
          html={feedback.riskExplanation}
        />
      )}
      <SimilarUsages
        similarUsages={claim?.similarUsages ?? []}
        type="claim"
        documentText={props.pages
          ?.map((p) => getDocumentText(p.text, feedback.feedbackLocation, p, props.hiddenHtmlText))
          .join(' ')}
      />
    </FeedbackCard>
  );
};

export default function FeedbackList(props: FeedbackListProps) {
  const { feedbacks, suggestionsAndSimilarUsages, evaluationType, isPublicView = false } = props;
  const { workspaceId } = useWorkspace();
  const { mutateAsync: actionPolicyFeedback } = api.feedback.actionPolicyFeedback.useMutation();
  const [exitingIds, setExitingIds] = useState<Set<string>>(() => new Set());
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => new Set());
  const dismissedIdsRef = useRef<Set<string>>(new Set());
  const exitLifecycleRef = useRef(new FeedbackListExitLifecycle());
  const isMountedRef = useRef(true);
  const documentIdRef = useRef(props.documentId);
  const onRemoveRef = useRef(props.onRemove);
  const onResolvedExitRef = useRef(props.onResolvedExit);
  const reloadDocRef = useRef(props.reloadDoc);
  const onLastIssueExitStartRef = useRef(props.onLastIssueExitStart);
  const onLastIssueExitRestoreRef = useRef(props.onLastIssueExitRestore);

  documentIdRef.current = props.documentId;
  onRemoveRef.current = props.onRemove;
  onResolvedExitRef.current = props.onResolvedExit;
  reloadDocRef.current = props.reloadDoc;
  onLastIssueExitStartRef.current = props.onLastIssueExitStart;
  onLastIssueExitRestoreRef.current = props.onLastIssueExitRestore;

  const releaseExit = useCallback((feedbackId: string) => {
    const nextDismissed = new Set(dismissedIdsRef.current);
    nextDismissed.delete(feedbackId);
    dismissedIdsRef.current = nextDismissed;
    setDismissedIds(nextDismissed);

    setExitingIds((prev) => {
      if (!prev.has(feedbackId)) return prev;
      const next = new Set(prev);
      next.delete(feedbackId);
      return next;
    });
  }, []);

  const applyCommit = useCallback(
    (commit: FeedbackExitCommit) => {
      if (commit.reason === 'resolve') {
        onResolvedExitRef.current?.(commit.relatedIds);
      } else {
        commit.relatedIds.forEach((id) => onRemoveRef.current?.(id));
      }
      void Promise.resolve(reloadDocRef.current()).finally(() => {
        if (isMountedRef.current) {
          releaseExit(commit.feedbackId);
        }
      });
    },
    [releaseExit],
  );
  const applyCommitRef = useRef(applyCommit);
  applyCommitRef.current = applyCommit;

  useEffect(() => {
    exitLifecycleRef.current.clear();
    setExitingIds(new Set());
    setDismissedIds(new Set());
    dismissedIdsRef.current = new Set();
  }, [props.documentId]);

  useEffect(() => {
    isMountedRef.current = true;
    // Only flush exits whose mutations already succeeded. Pending actions stay
    // in the lifecycle so a later confirm/fail can still update the parent cache.
    return () => {
      isMountedRef.current = false;
      for (const commit of exitLifecycleRef.current.unmount()) {
        applyCommitRef.current(commit);
      }
    };
  }, []);

  const listedFeedbacks = useMemo(
    () =>
      feedbacks.length ? getSortedFeedbacks(feedbacks, props.isVideo, props.isInShadowMode) : [],
    [feedbacks, props.isVideo, props.isInShadowMode],
  );

  const restoreExit = useCallback(
    (feedbackId: string) => {
      const wasLastOpenIssue = getIsLastOpenIssue({
        feedbacks: listedFeedbacks,
        dismissedIds: dismissedIdsRef.current,
        totalFeedbacks: props.totalFeedbacks,
        listedCount: feedbacks.length,
      });

      exitLifecycleRef.current.fail(feedbackId);
      if (isMountedRef.current) {
        releaseExit(feedbackId);
      }

      if (wasLastOpenIssue) {
        onLastIssueExitRestoreRef.current?.();
      }
    },
    [feedbacks.length, listedFeedbacks, props.totalFeedbacks, releaseExit],
  );

  const beginExit = useCallback(
    (
      feedbackId: string,
      reason: FeedbackExitReason,
      mutation: Promise<unknown>,
      relatedIds?: string[],
    ) => {
      const startedDocumentId = documentIdRef.current;
      exitLifecycleRef.current.begin(feedbackId, reason, relatedIds);
      const nextDismissed = new Set(dismissedIdsRef.current);
      nextDismissed.add(feedbackId);
      dismissedIdsRef.current = nextDismissed;

      setExitingIds((prev) => {
        const next = new Set(prev);
        next.add(feedbackId);
        return next;
      });
      setDismissedIds(nextDismissed);

      if (
        getIsLastOpenIssue({
          feedbacks: listedFeedbacks,
          dismissedIds: nextDismissed,
          totalFeedbacks: props.totalFeedbacks,
          listedCount: feedbacks.length,
        })
      ) {
        onLastIssueExitStartRef.current?.();
      }

      void mutation.then(
        () => {
          if (documentIdRef.current !== startedDocumentId) return;
          const commit = exitLifecycleRef.current.confirm(feedbackId);
          if (commit) applyCommitRef.current(commit);
        },
        () => {
          if (documentIdRef.current !== startedDocumentId) return;
          restoreExit(feedbackId);
        },
      );
    },
    [feedbacks.length, listedFeedbacks, props.totalFeedbacks, restoreExit],
  );

  const handleExitComplete = useCallback((feedbackId: string) => {
    const commit = exitLifecycleRef.current.animationComplete(feedbackId);
    if (commit) applyCommitRef.current(commit);
  }, []);

  const isRowVisible = useCallback(
    (feedbackId: string) => getIsRowVisible(feedbackId, dismissedIds, exitingIds),
    [dismissedIds, exitingIds],
  );

  const isRowExiting = useCallback(
    (feedbackId: string) => exitingIds.has(feedbackId),
    [exitingIds],
  );

  const listProps: FeedbackListItemProps = {
    ...props,
    beginExit,
    restoreExit,
    isRowExiting,
  };

  const sortedFeedbacks = listedFeedbacks.filter((feedback) => isRowVisible(feedback.id));

  // some feedback can be merged to one, so it's more reliable by counting the unique ids
  const totalSortedFeedbacks = useMemo(() => {
    const ids = new Set<string>();
    sortedFeedbacks.forEach((i) => {
      ids.add(i.id);
      i.feedbackIds?.forEach((j) => ids.add(j));
    });

    return ids.size;
  }, [sortedFeedbacks]);

  const hiddenFeedbackNumber = props.totalFeedbacks - totalSortedFeedbacks;

  const wrapExitRow = (feedbackId: string, node: ReactNode, isLastRow: boolean) => (
    <FeedbackListExitRow
      key={feedbackId}
      rowId={feedbackId}
      isExiting={exitingIds.has(feedbackId)}
      isCompactView={props.isCompactView}
      isLastRow={isLastRow}
      fastExit={getIsLastOpenIssue({
        feedbacks: sortedFeedbacks,
        dismissedIds,
        totalFeedbacks: props.totalFeedbacks,
        listedCount: feedbacks.length,
      })}
      onExitComplete={handleExitComplete}
    >
      {node}
    </FeedbackListExitRow>
  );

  return (
    <div className="grid gap-0">
      {!!sortedFeedbacks.length
        ? sortedFeedbacks.map((feedback, index) => {
            const isLastRow = index === sortedFeedbacks.length - 1;
            if (getIsClaim(feedback)) {
              return wrapExitRow(
                feedback.id,
                <ClaimFeedback {...listProps} feedback={feedback} />,
                isLastRow,
              );
            } else if (getIsTrademarkInstance(feedback)) {
              const isApproved = getIsApproved(feedback.trademark.approval);
              return wrapExitRow(
                feedback.id,
                <FeedbackCard
                  isClaim={false}
                  feedbackId={feedback.id}
                  isShadowFeedback={(feedback as any).isShadowFeedback}
                  shadowModelName={(feedback as any).shadowModelName}
                  isPrimaryFeedback={(feedback as any).isPrimaryFeedback}
                  primaryModelName={(feedback as any).primaryModelName}
                  policy={null}
                  brand={props.brand}
                  documentId={props.documentId}
                  evaluationType={evaluationType}
                  reloadDoc={props.reloadDoc}
                  isTest={props.isTest}
                  isResolvedFeedback={props.isResolvedFeedback}
                  riskLevel={feedback.riskLevel}
                  approvalStatus={isApproved ? 'Approved' : undefined}
                  videoStartTime={(feedback.location as FeedbackLocation).videoStartTime}
                  contentMedium={feedback.feedbackLocation}
                  isNew={feedback.isNew}
                  description={feedback.text}
                  title={SpecialPolicyFeedbackLabel.STANDARD_TRADEMARK.name}
                  color={SpecialPolicyFeedbackLabel.STANDARD_TRADEMARK.color}
                  onClick={(e) =>
                    props.onItemClick?.(e, feedback.text, feedback.location as FeedbackLocation)
                  }
                  onComment={
                    !isApproved &&
                    props.onComment &&
                    !props.isPublicView &&
                    !(feedback as any).isShadowFeedback
                      ? () => props.onComment?.(feedback)
                      : undefined
                  }
                  confirmButton={
                    isApproved ||
                    props.isPublicView ||
                    (feedback as any).isShadowFeedback ? undefined : (
                      <ConfirmTrademarkModal
                        tmInstanceId={feedback.id}
                        reloadDoc={props.reloadDoc}
                      />
                    )
                  }
                  beginExit={beginExit}
                  restoreExit={restoreExit}
                  isRowExiting={isRowExiting}
                  onRemove={props.onRemove}
                  isMultiPages={props.isMultiPages}
                  feedbackLocation={feedback.location as FeedbackLocation}
                  isPublicView={props.isPublicView}
                  isCompactView={props.isCompactView}
                  isInShadowMode={props.isInShadowMode}
                  onResolve={async () => {
                    await actionPolicyFeedback({
                      trademarkInstance: {
                        id: feedback.id,
                        status: props.isResolvedFeedback
                          ? StatusOptions.UNVERIFIED
                          : StatusOptions.SKIPPED,
                      },
                    });
                  }}
                >
                  <TrademarkFeedback
                    feedback={feedback}
                    proprietary={feedback.trademark.proprietary}
                    approval={feedback.trademark.approval}
                    isCompactView={props.isCompactView}
                  />
                </FeedbackCard>,
                isLastRow,
              );
            } else {
              const concatenateFeedbacks = getConcatenateFeedbacks(feedbacks, feedback);
              const similarUsages =
                suggestionsAndSimilarUsages?.results.policyFeedback.find((i) =>
                  concatenateFeedbacks.find((f) => f.id === i.id),
                )?.similarUsages ?? [];

              const isApproved =
                getIsApproved(feedback.approval) ||
                !!similarUsages?.find(
                  (c) => getIsApproved(c.approval) && c.confidence >= confidenceThreshold.high,
                );

              return wrapExitRow(
                feedback.id,
                <PolicyCard
                  riskLevel={feedback.riskLevel}
                  contentMedium={feedback.feedbackLocation}
                  isApproved={isApproved}
                  policy={{
                    id: feedback.policyId,
                    name: feedback.policy.name,
                    policyGroupId: feedback.policy.policyGroupId,
                    hasVisionPrompt: feedback.policy.steps?.some((s) => !!s.visionPrompt) || false,
                  }}
                  evaluationType={props.evaluationType}
                  color={feedback.policy.feedbackLabel?.color}
                  feedbackId={feedback.id}
                  formattedOutput={feedback.formattedOutput}
                  onComment={
                    props.onComment && !props.isPublicView
                      ? () => props.onComment?.(feedback)
                      : undefined
                  }
                  beginExit={beginExit}
                  restoreExit={restoreExit}
                  isRowExiting={isRowExiting}
                  onRemove={props.onRemove}
                  concatenateFeedbacks={concatenateFeedbacks}
                  feedbackLocation={feedback.location as FeedbackLocation}
                  isMultiPages={props.isMultiPages}
                  isContentScanner={props.isContentScanner}
                  explanation={feedback.explanation}
                  text={feedback.displayText ?? feedback.text ?? ''}
                  suggestions={
                    props.isPublicView ? [] : getConcatenatedSuggestions(sortedFeedbacks, feedback)
                  }
                  reloadDoc={props.reloadDoc}
                  brand={props.brand}
                  documentId={props.documentId}
                  isNew={feedback.isNew}
                  isTest={props.isTest}
                  isResolvedFeedback={props.isResolvedFeedback}
                  isAcknowledged={feedback.status === PolicyReviewStatus.ACKNOWLEDGED}
                  similarUsages={similarUsages}
                  isPublicView={props.isPublicView}
                  isCompactView={props.isCompactView}
                  isShadowFeedback={(feedback as any).isShadowFeedback}
                  shadowModelName={(feedback as any).shadowModelName}
                  isInShadowMode={props.isInShadowMode}
                  onClick={(e) =>
                    props.onItemClick?.(
                      e,
                      feedback.text ?? '',
                      feedback.location as FeedbackLocation,
                    )
                  }
                />,
                isLastRow,
              );
            }
          })
        : !!suggestionsAndSimilarUsages &&
          props.totalFeedbacks === 0 && (
            <div
              className={cn(
                'border-border bg-card space-y-1.5 rounded-xl border p-5 text-center shadow-[0_1px_2px_rgba(0,0,0,0.04)]',
                props.isCompactView && 'p-3',
              )}
            >
              <div className="bg-muted ring-border/60 mx-auto flex size-10 items-center justify-center rounded-[10px] ring-1">
                <CheckCircleIcon className="text-muted-foreground size-5" strokeWidth={2} />
              </div>
              <div className="text-foreground text-sm font-semibold tracking-tight">Looks good</div>
              <div className="text-muted-foreground text-sm">
                No issues spotted with{' '}
                {workspaceId ? (
                  <Link
                    href={policyEnginePath(workspaceId, '/groups')}
                    className="text-foreground underline underline-offset-2"
                  >
                    {evaluationType?.name}
                  </Link>
                ) : (
                  evaluationType?.name
                )}
                .
              </div>
            </div>
          )}
      {hiddenFeedbackNumber > 0 && !isPublicView && (
        <p className="text-muted-foreground text-xs">{hiddenFeedbackNumber} hidden by filters</p>
      )}
    </div>
  );
}
